import { useCallback } from 'react';
import { useAsync } from './useAsync';
import { apiClient } from '@/core/api';
import { isUnregisteredError } from '@/core/authErrors';
import type { MeResponse, OrganizationSummary } from '@/types/api';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgId } from '@/hooks/useOrgId';

function fetchMe() {
  return apiClient<MeResponse>('/users/me');
}

export function useMe() {
  const { isAuthenticated } = useAuth();
  const fn = useCallback(() => fetchMe(), []);
  return useAsync<MeResponse>(isAuthenticated ? fn : null);
}

/**
 * Org context for the app shell under ``/org/:orgId/…``.
 *
 * Active org is always the URL segment; membership is validated by ``OrgRouteGuard``.
 */
export function useOrgBootstrap(): {
  ready: boolean;
  needsSetup: boolean;
  orgs: OrganizationSummary[];
  activeOrgId: string;
  loading: boolean;
  error: string | null;
} {
  const orgId = useOrgId();
  const { isAuthenticated } = useAuth();
  const fn = useCallback(() => fetchMe(), []);
  const { data, loading, error } = useAsync<MeResponse>(isAuthenticated ? fn : null);

  const orgs = data?.organizations ?? [];
  const isMissingRegistration = error != null && isUnregisteredError(error);

  if (isMissingRegistration) {
    return {
      ready: false,
      needsSetup: true,
      orgs,
      activeOrgId: orgId,
      loading: false,
      error: null,
    };
  }

  return {
    ready: true,
    needsSetup: false,
    orgs,
    activeOrgId: orgId,
    loading,
    error,
  };
}
